import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../api/endpoints';

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const refreshCount = useCallback(async () => {
    try {
      const data = await api.getUnreadCount();
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const interval = setInterval(refreshCount, 30000);
    return () => clearInterval(interval);
  }, [refreshCount]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        const data = await api.getNotifications();
        setNotifications(data);
        if (unreadCount > 0) {
          await api.markNotificationsRead();
          setUnreadCount(0);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    }
  };

  const handleNotificationClick = (n) => {
    setOpen(false);
    if (n.board_key && n.ticket) {
      navigate(`/board/${n.board_key}?ticket=${n.ticket}`);
    }
  };

  return (
    <div style={{ position: 'relative' }} ref={containerRef}>
      <button
        type="button"
        className="notif-bell"
        onClick={handleToggle}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        🔔
        {unreadCount > 0 && <span className="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>
      {open && (
        <div className="notif-dropdown">
          {notifications.length === 0 && (
            <div className="empty-state" style={{ padding: '1rem' }}>No notifications yet.</div>
          )}
          {notifications.map((n) => (
            <div
              key={n.id}
              className={`notif-item ${n.is_read ? '' : 'unread'}`}
              role={n.ticket ? 'button' : undefined}
              tabIndex={n.ticket ? 0 : undefined}
              style={n.ticket ? { cursor: 'pointer' } : undefined}
              onClick={() => handleNotificationClick(n)}
              onKeyDown={(e) => {
                if (n.ticket && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleNotificationClick(n);
                }
              }}
            >
              {n.ticket_key && <span className="ticket-key" style={{ marginRight: '0.35rem' }}>{n.ticket_key}</span>}
              {n.message}
              {n.created_at && (
                <div style={{ fontSize: '0.72rem', color: '#8b97a5', marginTop: '0.2rem' }} title={new Date(n.created_at).toLocaleString()}>
                  {formatWhen(n.created_at)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
